import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, ChevronLeft, ChevronRight } from "lucide-react";

const CATS = [
  { k: "bounced", l: "Bounced addresses", desc: "Blocked from all outreach until fixed" },
  { k: "missing_email", l: "Missing emails", desc: "Drafts with no recipient — need research" },
  { k: "duplicate_coaches", l: "Duplicate coaches", desc: "Same name + organization more than once" },
  { k: "duplicate_jobs", l: "Duplicate jobs", desc: "Same program + position more than once" },
  { k: "missing_org", l: "Missing organizations", desc: "Coach profiles with no program attached" },
  { k: "unverified_sport", l: "Unverified sports", desc: "Drafts awaiting lacrosse verification" },
  { k: "non_lacrosse", l: "Non-lacrosse records", desc: "Quarantined — wrong sport detected" },
  { k: "enrichment_failed", l: "Failed enrichment", desc: "AI couldn't fix these — needs a human" },
  { k: "ai_corrected", l: "AI-corrected, awaiting confirm", desc: "Claude fixed these — confirm the corrections" },
];

export default function DataQuality() {
  const [params, setParams] = useSearchParams();
  const cat = params.get("cat") || "bounced";
  const [summary, setSummary] = useState(null);
  const [skip, setSkip] = useState(0);
  const [list, setList] = useState(null);
  const [fixing, setFixing] = useState(false);
  const [dedupeBusy, setDedupeBusy] = useState(null);
  const limit = 25;

  const loadSummary = () => api.get("/admin/data-quality").then(({ data }) => setSummary(data)).catch(() => {});
  useEffect(() => { loadSummary(); }, []);
  useEffect(() => {
    setList(null);
    api.get(`/admin/data-quality/${cat}?skip=${skip}&limit=${limit}`).then(({ data }) => setList(data)).catch(() => {});
  }, [cat, skip]);
  useEffect(() => { setSkip(0); }, [cat]);

  const countFor = (k) => {
    if (!summary) return null;
    return { bounced: summary.bounced, missing_email: summary.missing_email,
      duplicate_coaches: summary.duplicate_coaches, duplicate_jobs: summary.duplicate_jobs,
      missing_org: summary.missing_org, unverified_sport: summary.unverified_sport,
      non_lacrosse: summary.non_lacrosse, enrichment_failed: summary.enrichment_failed,
      ai_corrected: summary.ai_corrected_awaiting_confirm }[k];
  };

  const runBounceFix = async () => {
    setFixing(true);
    try {
      const { data } = await api.post("/admin/bounces/fix");
      if (data.started === 0) toast.info("No unfixed bounces to work on");
      else toast.success(`Claude is working on ${data.started} bounced address${data.started > 1 ? "es" : ""} — refresh in ~1 min`);
    } catch (e) { toast.error(e.response?.data?.detail || "Fix run failed"); }
    setFixing(false);
  };

  const runDedupe = async (kind) => {
    const url = kind === "jobs" ? "/admin/jobs/dedupe" : "/admin/coaches/dedupe-exact";
    if (!window.confirm(`Run the ${kind === "jobs" ? "job" : "coach"} de-duplicator? Confirmed duplicates will be merged/removed. This cannot be undone.`)) return;
    setDedupeBusy(kind);
    try {
      const { data } = await api.post(url);
      toast.success(`De-dupe complete: ${JSON.stringify(data).slice(0, 120)}`);
      loadSummary();
    } catch (e) { toast.error(e.response?.data?.detail || "De-dupe failed"); }
    setDedupeBusy(null);
  };

  return (
    <div className="px-4 md:px-8 py-8 max-w-6xl" data-testid="hq-data-quality-page">
      <div className="text-xs tracking-widest text-volt">ADMIN HQ · DATA</div>
      <h1 className="heading text-4xl mt-1">DATA QUALITY</h1>
      <p className="text-neutral-400 mt-2 text-sm max-w-2xl">Every data problem in one place, with a clear resolution path.</p>

      <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="dq-cards">
        {CATS.map((c) => {
          const v = countFor(c.k);
          const active = cat === c.k;
          return (
            <button key={c.k} data-testid={`dq-card-${c.k}`} onClick={() => setParams({ cat: c.k })}
              className={`text-left border p-3 transition-colors ${active ? "border-blaze bg-neutral-900/50" : v > 0 ? "border-amber-800/50 bg-appcard hover:border-volt" : "border-neutral-800 bg-appcard hover:border-neutral-600"}`}>
              <div className={`heading text-2xl ${v > 0 ? "text-amber-400" : "text-neutral-500"}`}>{v ?? "…"}</div>
              <div className="text-[11px] text-white mt-0.5">{c.l}</div>
              <div className="text-[10px] text-neutral-500 mt-0.5">{c.desc}</div>
            </button>
          );
        })}
      </div>

      {/* Resolution actions */}
      <div className="mt-5 flex flex-wrap gap-2" data-testid="dq-actions">
        {cat === "bounced" && (
          <button data-testid="dq-fix-bounces" onClick={runBounceFix} disabled={fixing}
            className="min-h-[38px] px-4 border border-volt text-volt text-xs tracking-widest hover:bg-volt hover:text-white disabled:opacity-50 flex items-center gap-1.5">
            <RefreshCw size={12} className={fixing ? "animate-spin" : ""} /> {fixing ? "CLAUDE FIXING…" : `FIX WITH AI (${summary?.bounced_unfixed ?? 0} UNFIXED)`}
          </button>
        )}
        {cat === "duplicate_jobs" && (
          <button data-testid="dq-dedupe-jobs" onClick={() => runDedupe("jobs")} disabled={dedupeBusy === "jobs"}
            className="min-h-[38px] px-4 border border-red-800 text-red-400 text-xs tracking-widest hover:border-red-500 disabled:opacity-50">
            {dedupeBusy === "jobs" ? "RUNNING…" : "MERGE DUPLICATE JOBS…"}
          </button>
        )}
        {cat === "duplicate_coaches" && (
          <button data-testid="dq-dedupe-coaches" onClick={() => runDedupe("coaches")} disabled={dedupeBusy === "coaches"}
            className="min-h-[38px] px-4 border border-red-800 text-red-400 text-xs tracking-widest hover:border-red-500 disabled:opacity-50">
            {dedupeBusy === "coaches" ? "RUNNING…" : "MERGE EXACT DUPLICATE COACHES…"}
          </button>
        )}
        {(cat === "missing_email" || cat === "unverified_sport" || cat === "non_lacrosse") && (
          <a href={`/hq/queue?bucket=${cat === "missing_email" ? "needs_email" : cat === "unverified_sport" ? "needs_sport" : "quarantined"}`}
            className="min-h-[38px] px-4 border border-volt text-volt text-xs tracking-widest hover:bg-volt hover:text-white flex items-center">
            RESOLVE IN WORK QUEUE →
          </a>
        )}
      </div>

      {/* Records */}
      {!list ? (
        <div className="mt-6 text-neutral-500 text-sm">Loading…</div>
      ) : list.items.length === 0 ? (
        <div className="mt-6 border border-dashed border-neutral-800 p-8 text-neutral-500" data-testid="dq-empty">
          Nothing here — this category is clean.
        </div>
      ) : (
        <>
          <div className="mt-4 space-y-1.5" data-testid="dq-list">
            {list.items.map((it, i) => <DQRow key={i} cat={cat} it={it} idx={i} />)}
          </div>
          {list.total != null && list.total > limit && (
            <div className="mt-3 flex items-center gap-3 text-[11px] text-neutral-500">
              <button disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))} className="border border-neutral-700 px-2.5 py-1.5 disabled:opacity-40"><ChevronLeft size={12} /></button>
              <span>{skip + 1}–{Math.min(skip + limit, list.total)} of {list.total}</span>
              <button disabled={skip + limit >= list.total} onClick={() => setSkip(skip + limit)} className="border border-neutral-700 px-2.5 py-1.5 disabled:opacity-40"><ChevronRight size={12} /></button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function DQRow({ cat, it, idx }) {
  if (["bounced", "enrichment_failed", "ai_corrected"].includes(cat)) {
    return (
      <div className="border border-neutral-900 px-3 py-2 text-xs flex items-center gap-2 flex-wrap" data-testid={`dq-row-${idx}`}>
        <span className="text-neutral-200 font-mono">{it.email}</span>
        <span className="text-[9px] tracking-widest px-1.5 py-0.5 border border-red-600 text-red-500">{(it.event || "bounced").toUpperCase()}</span>
        {it.fix_status === "fixed" ? (
          <span className="text-[10px] text-volt">✓ AI FIX → <span className="font-mono">{it.corrected_email}</span></span>
        ) : it.fix_status === "no_fix_found" ? (
          <span className="text-[10px] text-neutral-500" title={it.fix_reason}>NO FIX FOUND — correct manually</span>
        ) : it.fix_status === "skipped_test" ? (
          <span className="text-[10px] text-neutral-600">TEST ADDRESS</span>
        ) : (
          <span className="text-[10px] text-amber-400">AWAITING AI FIX</span>
        )}
        <span className="text-[10px] text-neutral-600 ml-auto">{it.flagged_at ? new Date(it.flagged_at).toLocaleDateString() : ""}</span>
      </div>
    );
  }
  if (cat === "duplicate_coaches" || cat === "duplicate_jobs") {
    return (
      <div className="border border-neutral-900 px-3 py-2 text-xs flex items-center gap-2 flex-wrap" data-testid={`dq-row-${idx}`}>
        <span className="text-white">{it.name || it.program_name}</span>
        <span className="text-neutral-500">{it.organization || it.title}</span>
        <span className="text-[9px] tracking-widest px-1.5 py-0.5 border border-amber-600 text-amber-400">{it.count}× COPIES</span>
        {cat === "duplicate_coaches" && it.ids?.[0] && (
          <a href={`/hq/coaches?q=${encodeURIComponent(it.name || "")}`} className="ml-auto text-[10px] text-volt hover:underline">REVIEW →</a>
        )}
        {cat === "duplicate_jobs" && it.ids?.[0] && (
          <a href={`/hq/jobs?q=${encodeURIComponent(it.program_name || "")}`} className="ml-auto text-[10px] text-volt hover:underline">REVIEW →</a>
        )}
      </div>
    );
  }
  if (cat === "missing_org") {
    return (
      <div className="border border-neutral-900 px-3 py-2 text-xs flex items-center gap-2 flex-wrap" data-testid={`dq-row-${idx}`}>
        <span className="text-white">{it.name || "(unnamed)"}</span>
        <span className="text-neutral-500">{it.current_role || "—"}</span>
        <span className="text-neutral-600">{it.source || ""}</span>
        <a href={`/hq/coaches/${it.id}/edit`} className="ml-auto text-[10px] text-volt hover:underline">FIX →</a>
      </div>
    );
  }
  return (
    <div className="border border-neutral-900 px-3 py-2 text-xs flex items-center gap-2 flex-wrap" data-testid={`dq-row-${idx}`}>
      <span className="text-white">{it.recipient_name}</span>
      <span className="text-neutral-500 truncate max-w-[300px]">{it.subject}</span>
      <span className="text-neutral-600">{it.context}</span>
    </div>
  );
}
